<script>
	import { getContext, onDestroy } from 'svelte';
	import { mapbox, key } from '../mapbox.js';
	import { truckMarkers } from '../../stores.js';

	const { getMap } = getContext(key);
	const map = getMap();

	export let lat;
	export let lon;
	export let label = '';
	export let id = null;
	export let direction = null;
	export let rego = '';
	export let loadsCount = 0;
	export let loads = [];
	export let driver = null;
	export let trailer = null;
	export let destination = null;
	export let capacity = null;
	export let materialName = '';
	export let customerName = '';
	export let jobNumber = '';

	let loadsList = [];
	$: loadsList = Array.isArray(loads) ? loads : [];
	let effectiveRego = 'TRUCK';
	$: effectiveRego = rego || label || (id ? String(id).split('-').pop() : '') || 'TRUCK';
	let effectiveLoads = 0;
	$: effectiveLoads = (Array.isArray(loads) && loads.length > 0) ? loads.length : Number(loadsCount ?? 0);

	// Helpers for rich popup rendering
	function escapeHTML(str) {
		if (str == null) return '';
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	function formatTime(timeStr) {
		if (!timeStr) return '—';
		try {
			const cleanStr = String(timeStr).replace(' ', 'T');
			const d = new Date(cleanStr);
			if (!isNaN(d.getTime())) {
				return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
			}
			if (String(timeStr).includes(' ')) {
				return String(timeStr).split(' ')[1];
			}
			return String(timeStr);
		} catch (e) {
			return String(timeStr);
		}
	}

	function calculateDuration(loadStr, unloadStr) {
		if (!loadStr || !unloadStr) return null;
		try {
			const d1 = new Date(String(loadStr).replace(' ', 'T'));
			const d2 = new Date(String(unloadStr).replace(' ', 'T'));
			if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;
			const diffMs = Math.abs(d2 - d1);
			const totalSecs = Math.floor(diffMs / 1000);
			const mins = Math.floor(totalSecs / 60);
			const secs = totalSecs % 60;
			if (mins >= 60) {
				const hrs = Math.floor(mins / 60);
				const remMins = mins % 60;
				return `${hrs}h ${remMins}m`;
			}
			return `${mins}m ${secs}s`;
		} catch (e) {
			return null;
		}
	}

	function buildPopupHTML() {
		const currentLoads = Array.isArray(loads) ? loads : (Array.isArray(loadsList) ? loadsList : []);
		const currentRego = rego || label || (id ? String(id).split('-').pop() : '') || 'TRUCK';
		const currentLoadsCount = currentLoads.length > 0 ? currentLoads.length : Number(loadsCount ?? 0);
		const driverName = driver ? (driver.name || (typeof driver === 'string' ? driver : '')) : '';
		const trailerId = trailer ? (trailer.id || (typeof trailer === 'string' ? trailer : '')) : '';
		const latNum = parseFloat(lat) || 0;
		const lonNum = parseFloat(lon) || 0;

		let destText = '';
		if (destination) {
			const siteType = destination.site === 'dispose' ? 'Disposal Site' : (destination.site === 'load' ? 'Load Site' : (destination.site || ''));
			const suburb = destination.suburb ? ` (${destination.suburb})` : '';
			const dist = destination.distance != null ? ` - ${destination.distance}km` : '';
			destText = `${siteType}${suburb}${dist}`;
		}

		let loadsHTML = '';
		if (currentLoads.length === 0) {
			loadsHTML = `
				<div class="tt-loads-empty">
					<span class="tt-empty-icon">⏳</span>
					<span>No load cycles completed yet today.</span>
				</div>
			`;
		} else {
			const rowsHTML = currentLoads.map((l, idx) => {
				const num = idx + 1;
				const loadT = formatTime(l?.load_time);
				const unloadT = l?.unload_time ? formatTime(l.unload_time) : '<span class="tt-in-transit">In Transit</span>';
				const dur = calculateDuration(l?.load_time, l?.unload_time) || '—';
				const docketTag = l?.docket ? `<span class="tt-docket-tag">#${escapeHTML(l.docket)}</span>` : '';
				return `
					<tr>
						<td class="tt-td-num">#${num}</td>
						<td class="tt-td-time tt-load-time">
							<span class="tt-time-dot green"></span>
							<span>${loadT}</span>
						</td>
						<td class="tt-td-time tt-unload-time">
							<span class="tt-time-dot red"></span>
							<span>${unloadT}</span>
							${docketTag}
						</td>
						<td class="tt-td-dur">${dur}</td>
					</tr>
				`;
			}).join('');

			loadsHTML = `
				<div class="tt-loads-table-wrap" onwheel="event.stopPropagation()">
					<table class="tt-loads-table">
						<thead>
							<tr>
								<th>#</th>
								<th>Load Time</th>
								<th>Unload Time</th>
								<th>Duration</th>
							</tr>
						</thead>
						<tbody>
							${rowsHTML}
						</tbody>
					</table>
				</div>
			`;
		}

		return `
			<div class="tt-truck-popup-card">
				<div class="tt-truck-popup-header">
					<div class="tt-truck-header-main">
						<div class="tt-truck-rego-tag">${escapeHTML(currentRego)}</div>
						<div class="tt-truck-cap-tag">${capacity ? `${escapeHTML(capacity)}t Capacity` : 'Heavy Vehicle'}</div>
						${trailerId ? `<div class="tt-truck-trailer-tag">+ Trailer: ${escapeHTML(trailerId)}</div>` : ''}
					</div>
					${driverName ? `
						<div class="tt-truck-driver-row">
							<svg class="tt-popup-svg" viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
								<path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0-2.21-3.58-4-6-4s-6 1.79-6 4v1h12v-1z"/>
							</svg>
							<span>${escapeHTML(driverName)}</span>
						</div>
					` : ''}
					${(materialName || jobNumber || customerName) ? `
						<div class="tt-truck-job-row">
							${jobNumber ? `<span class="tt-job-pill">Job #${escapeHTML(String(jobNumber))}</span>` : ''}
							${materialName ? `<span class="tt-mat-pill">${escapeHTML(materialName)}</span>` : ''}
							${customerName ? `<span class="tt-cust-pill">${escapeHTML(customerName)}</span>` : ''}
						</div>
					` : ''}
				</div>

				<div class="tt-truck-status-bar">
					<div class="tt-status-item">
						<span class="tt-status-label">Total Loads Today</span>
						<span class="tt-status-val tt-loads-val">${currentLoadsCount}</span>
					</div>
					${destText ? `
						<div class="tt-status-item tt-status-dest">
							<span class="tt-status-label">Next Destination</span>
							<span class="tt-status-val">${escapeHTML(destText)}</span>
						</div>
					` : ''}
				</div>

				<div class="tt-truck-loads-section">
					<div class="tt-loads-section-title">
						<span>Completed Loads Log</span>
						<span class="tt-loads-count-pill">${currentLoadsCount} ${currentLoadsCount === 1 ? 'load' : 'loads'}</span>
					</div>
					${loadsHTML}
				</div>

				<div class="tt-truck-popup-footer">
					<div class="tt-footer-coords">GPS: ${latNum.toFixed(4)}, ${lonNum.toFixed(4)}</div>
					${direction ? `<div class="tt-footer-dir">Heading ${escapeHTML(direction.toUpperCase())}</div>` : ''}
				</div>
			</div>
		`;
	}

	// Create root DOM element for each marker
	const el = document.createElement('div');
	el.className = 'marker map-icon-marker truck-marker-root';
	el.style.overflow = 'visible';
	el.addEventListener('click', (e) => {
		e.stopPropagation();
		if (marker) {
			marker.togglePopup();
		}
	});

	if (direction === "east") {
		el.style.backgroundImage = 'url(./assests/truck_east.png)';
	} else {
		el.style.backgroundImage = 'url(./assests/truck_west.png)';
	}

	// 1. Loads count badge (Top-Left of the truck)
	const initialLoadsCount = (Array.isArray(loads) && loads.length > 0) ? loads.length : Number(loadsCount ?? 0);
	const loadsBadgeEl = document.createElement('div');
	loadsBadgeEl.className = 'truck-loads-badge';
	loadsBadgeEl.textContent = String(initialLoadsCount);
	loadsBadgeEl.title = `${initialLoadsCount} loads completed`;
	el.appendChild(loadsBadgeEl);

	// 2. Yellow license plate with rego (Below the truck)
	const initialRego = rego || label || (id ? String(id).split('-').pop() : '') || 'TRUCK';
	const regoPlateEl = document.createElement('div');
	regoPlateEl.className = 'truck-rego-plate';
	regoPlateEl.textContent = initialRego;
	el.appendChild(regoPlateEl);

	const popup = new mapbox.Popup({
		offset: 25,
		closeButton: true,
		closeOnClick: false,
		className: 'tt-truck-info-popup',
		maxWidth: '380px'
	}).setHTML(buildPopupHTML());

	let marker = null;
	const latNum = parseFloat(lat);
	const lonNum = parseFloat(lon);

	// Tracking variables for smooth transition animation
	let lastTargetLat = null;
	let lastTargetLng = null;
	let lastUpdateTime = null;
	let animFrameId = null;

	if (map && !isNaN(latNum) && !isNaN(lonNum)) {
		marker = new mapbox.Marker(el).setLngLat([lonNum, latNum]).setPopup(popup).addTo(map);
		lastTargetLat = latNum;
		lastTargetLng = lonNum;
		lastUpdateTime = (typeof performance !== 'undefined') ? performance.now() : Date.now();

		$truckMarkers = [...$truckMarkers.filter(m => m.marker !== marker), {
			marker: marker,
			label: label,
			rego: effectiveRego,
			loadsCount: effectiveLoads,
			id: id
		}];
	}

	/**
	 * Smoothly moves the marker from its current position to the new coordinates.
	 * Rather than stepping / jumping instantly, it smoothly interpolates over time.
	 */
	function animateTo(targetLat, targetLng) {
		if (!marker) return;

		const currentPos = marker.getLngLat();
		const startLng = currentPos.lng;
		const startLat = currentPos.lat;

		const dLng = targetLng - startLng;
		const dLat = targetLat - startLat;

		if (Math.abs(dLng) < 1e-7 && Math.abs(dLat) < 1e-7) {
			return;
		}

		if (Math.hypot(dLng, dLat) > 0.1) {
			if (animFrameId) {
				cancelAnimationFrame(animFrameId);
				animFrameId = null;
			}
			marker.setLngLat([targetLng, targetLat]);
			return;
		}

		const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
		let duration = 2000;
		if (lastUpdateTime != null) {
			const delta = now - lastUpdateTime;
			if (delta > 500 && delta < 10000) {
				duration = Math.min(delta * 0.95, 4500);
			}
		}
		lastUpdateTime = now;

		if (animFrameId) {
			cancelAnimationFrame(animFrameId);
			animFrameId = null;
		}

		const startTime = (typeof performance !== 'undefined') ? performance.now() : Date.now();

		function frame(currentTime) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			const curLng = startLng + dLng * progress;
			const curLat = startLat + dLat * progress;

			marker.setLngLat([curLng, curLat]);

			if (progress < 1) {
				animFrameId = requestAnimationFrame(frame);
			} else {
				animFrameId = null;
			}
		}

		animFrameId = requestAnimationFrame(frame);
	}

	// Dynamically animate coordinates when polled / updated
	$: if (marker && lat != null && lon != null) {
		const parsedLat = parseFloat(lat);
		const parsedLon = parseFloat(lon);
		if (!isNaN(parsedLat) && !isNaN(parsedLon)) {
			if (parsedLat !== lastTargetLat || parsedLon !== lastTargetLng) {
				lastTargetLat = parsedLat;
				lastTargetLng = parsedLon;
				animateTo(parsedLat, parsedLon);
			}
		}
	}

	// Dynamically update direction image if heading changes
	$: if (el && direction) {
		if (direction === "east") {
			el.style.backgroundImage = 'url(./assests/truck_east.png)';
		} else {
			el.style.backgroundImage = 'url(./assests/truck_west.png)';
		}
	}

	// Dynamically update rego license plate text
	$: if (regoPlateEl && effectiveRego) {
		regoPlateEl.textContent = effectiveRego;
	}

	// Dynamically update loads count badge
	$: if (loadsBadgeEl) {
		loadsBadgeEl.textContent = String(effectiveLoads);
		loadsBadgeEl.title = `${effectiveLoads} loads completed`;
	}

	// Dynamically update popup HTML if any truck data updates
	$: if (popup && (effectiveRego || effectiveLoads != null || loadsList || destination || driver || materialName || jobNumber)) {
		popup.setHTML(buildPopupHTML());
	}

	onDestroy(() => {
		if (animFrameId) {
			cancelAnimationFrame(animFrameId);
			animFrameId = null;
		}
		if (marker) {
			marker.remove();
		}
		$truckMarkers = $truckMarkers.filter(m => m.marker !== marker);
	});
</script>

<style>
	:global(.marker.map-icon-marker.truck-marker-root) {
		overflow: visible !important;
		position: absolute !important;
	}

	:global(.truck-loads-badge) {
		position: absolute !important;
		top: -6px !important;
		left: -6px !important;
		z-index: 25 !important;
		min-width: clamp(16px, calc(var(--map-icon-size, 50px) * 0.38), 22px) !important;
		height: clamp(16px, calc(var(--map-icon-size, 50px) * 0.38), 22px) !important;
		padding: 0 clamp(3px, calc(var(--map-icon-size, 50px) * 0.08), 5px) !important;
		border-radius: 9999px !important;
		background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%) !important;
		color: #ffffff !important;
		border: 1.5px solid #ffffff !important;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.45) !important;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
		font-size: clamp(9px, calc(var(--map-icon-size, 50px) * 0.22), 11px) !important;
		font-weight: 800 !important;
		line-height: 1 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		box-sizing: border-box !important;
		user-select: none !important;
		pointer-events: none !important;
	}

	:global(.truck-rego-plate) {
		position: absolute !important;
		top: calc(100% + clamp(1px, calc(var(--map-icon-size, 50px) * 0.04), 3px)) !important;
		left: 50% !important;
		transform: translateX(-50%) !important;
		z-index: 20 !important;
		background: linear-gradient(180deg, #FFE033 0%, #FFCC00 100%) !important;
		color: #000000 !important;
		border: 1.5px solid #111111 !important;
		border-radius: 3px !important;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.35) !important;
		font-family: "Consolas", "Lucida Console", "Liberation Mono", "Arial Black", monospace, sans-serif !important;
		font-size: clamp(9px, calc(var(--map-icon-size, 50px) * 0.22), 11.5px) !important;
		font-weight: 900 !important;
		letter-spacing: 0.6px !important;
		text-transform: uppercase !important;
		padding: clamp(0.5px, calc(var(--map-icon-size, 50px) * 0.03), 1.5px) clamp(3px, calc(var(--map-icon-size, 50px) * 0.1), 6px) !important;
		line-height: 1.2 !important;
		white-space: nowrap !important;
		user-select: none !important;
		pointer-events: none !important;
	}

	/* Rich Truck Information Mapbox Popup */
	:global(.mapboxgl-popup.tt-truck-info-popup) {
		z-index: 60 !important;
	}

	:global(.tt-truck-info-popup .mapboxgl-popup-content) {
		padding: 0 !important;
		border-radius: 10px !important;
		box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28), 0 4px 10px rgba(0, 0, 0, 0.15) !important;
		border: 1px solid #cbd5e1 !important;
		overflow: hidden !important;
		font-family: inherit !important;
		background: #ffffff !important;
		width: 360px !important;
		max-width: 90vw !important;
	}

	:global(.tt-truck-info-popup .mapboxgl-popup-close-button) {
		font-size: 16px !important;
		color: #64748b !important;
		padding: 4px 8px !important;
		top: 6px !important;
		right: 6px !important;
		z-index: 10 !important;
		border-radius: 4px !important;
		transition: all 0.15s ease !important;
	}

	:global(.tt-truck-info-popup .mapboxgl-popup-close-button:hover) {
		background-color: #f1f5f9 !important;
		color: #0f172a !important;
	}

	:global(.tt-truck-popup-card) {
		display: flex !important;
		flex-direction: column !important;
		width: 100% !important;
	}

	:global(.tt-truck-popup-header) {
		padding: 10px 14px 8px 14px !important;
		background: #ffffff !important;
		border-bottom: 1px solid #e2e8f0 !important;
	}

	:global(.tt-truck-header-main) {
		display: flex !important;
		align-items: center !important;
		gap: 8px !important;
	}

	:global(.tt-truck-header-main .tt-truck-rego-tag) {
		background: linear-gradient(180deg, #FFE033 0%, #FFCC00 100%) !important;
		color: #000000 !important;
		border: 1.5px solid #111111 !important;
		border-radius: 3px !important;
		padding: 1px 7px !important;
		font-family: "Consolas", "Lucida Console", monospace, sans-serif !important;
		font-weight: 900 !important;
		font-size: 12px !important;
		letter-spacing: 0.6px !important;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25) !important;
	}

	:global(.tt-truck-cap-tag) {
		background: #f1f5f9 !important;
		color: #475569 !important;
		border: 1px solid #e2e8f0 !important;
		border-radius: 4px !important;
		padding: 2px 7px !important;
		font-size: 11px !important;
		font-weight: 700 !important;
	}

	:global(.tt-truck-trailer-tag) {
		background: #fef3c7 !important;
		color: #92400e !important;
		border: 1px solid #fde68a !important;
		border-radius: 4px !important;
		padding: 2px 6px !important;
		font-size: 10.5px !important;
		font-weight: 700 !important;
	}

	:global(.tt-truck-driver-row) {
		display: flex !important;
		align-items: center !important;
		gap: 5px !important;
		font-size: 12px !important;
		color: #334155 !important;
		font-weight: 600 !important;
		margin-top: 5px !important;
	}

	:global(.tt-popup-svg) {
		color: #64748b !important;
		flex-shrink: 0 !important;
	}

	:global(.tt-truck-job-row) {
		display: flex !important;
		align-items: center !important;
		gap: 6px !important;
		margin-top: 5px !important;
		flex-wrap: wrap !important;
	}

	:global(.tt-job-pill) {
		background: #eff6ff !important;
		color: #1d4ed8 !important;
		border: 1px solid #bfdbfe !important;
		font-size: 10px !important;
		font-weight: 700 !important;
		padding: 1px 6px !important;
		border-radius: 3px !important;
	}

	:global(.tt-mat-pill) {
		background: #f8fafc !important;
		color: #475569 !important;
		border: 1px solid #cbd5e1 !important;
		font-size: 10px !important;
		font-weight: 600 !important;
		padding: 1px 6px !important;
		border-radius: 3px !important;
	}

	:global(.tt-cust-pill) {
		background: #f0fdf4 !important;
		color: #166534 !important;
		border: 1px solid #bbf7d0 !important;
		font-size: 10px !important;
		font-weight: 600 !important;
		padding: 1px 6px !important;
		border-radius: 3px !important;
	}

	:global(.tt-truck-status-bar) {
		display: flex !important;
		align-items: center !important;
		justify-content: space-between !important;
		background: #f8fafc !important;
		padding: 6px 14px !important;
		border-bottom: 1px solid #e2e8f0 !important;
		gap: 8px !important;
	}

	:global(.tt-status-item) {
		display: flex !important;
		flex-direction: column !important;
	}

	:global(.tt-status-label) {
		font-size: 9.5px !important;
		text-transform: uppercase !important;
		letter-spacing: 0.04em !important;
		color: #64748b !important;
		font-weight: 700 !important;
	}

	:global(.tt-status-val) {
		font-size: 12px !important;
		font-weight: 700 !important;
		color: #0f172a !important;
	}

	:global(.tt-loads-val) {
		color: #1d4ed8 !important;
		font-size: 14px !important;
	}

	:global(.tt-status-dest .tt-status-val) {
		max-width: 190px !important;
		white-space: nowrap !important;
		overflow: hidden !important;
		text-overflow: ellipsis !important;
	}

	:global(.tt-truck-loads-section) {
		padding: 0 !important;
	}

	:global(.tt-loads-section-title) {
		display: flex !important;
		align-items: center !important;
		justify-content: space-between !important;
		padding: 8px 14px 6px 14px !important;
		font-size: 10.5px !important;
		font-weight: 700 !important;
		text-transform: uppercase !important;
		letter-spacing: 0.03em !important;
		color: #475569 !important;
		background: #ffffff !important;
	}

	:global(.tt-loads-count-pill) {
		background: #1e3a8a !important;
		color: #ffffff !important;
		font-size: 9.5px !important;
		font-weight: 800 !important;
		padding: 1px 6px !important;
		border-radius: 9999px !important;
	}

	:global(.tt-loads-empty) {
		padding: 18px 14px !important;
		text-align: center !important;
		font-size: 11.5px !important;
		color: #64748b !important;
		display: flex !important;
		flex-direction: column !important;
		align-items: center !important;
		gap: 6px !important;
		background: #fafafa !important;
	}

	:global(.tt-empty-icon) {
		font-size: 18px !important;
	}

	:global(.tt-loads-table-wrap) {
		max-height: 180px !important;
		overflow-y: auto !important;
		border-top: 1px solid #f1f5f9 !important;
	}

	:global(.tt-loads-table) {
		width: 100% !important;
		border-collapse: collapse !important;
		font-size: 11px !important;
		text-align: left !important;
	}

	:global(.tt-loads-table thead th) {
		position: sticky !important;
		top: 0 !important;
		background: #f8fafc !important;
		color: #475569 !important;
		font-weight: 700 !important;
		font-size: 9.5px !important;
		text-transform: uppercase !important;
		letter-spacing: 0.03em !important;
		padding: 5px 8px !important;
		border-bottom: 1px solid #e2e8f0 !important;
		z-index: 5 !important;
	}

	:global(.tt-loads-table tbody td) {
		padding: 5px 8px !important;
		border-bottom: 1px solid #f1f5f9 !important;
		color: #1e293b !important;
		white-space: nowrap !important;
	}

	:global(.tt-loads-table tbody tr:hover) {
		background-color: #f8fafc !important;
	}

	:global(.tt-td-num) {
		font-weight: 700 !important;
		color: #64748b !important;
		width: 28px !important;
	}

	:global(.tt-td-time) {
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace !important;
		font-size: 10.5px !important;
		font-weight: 600 !important;
	}

	:global(.tt-time-dot) {
		width: 6px !important;
		height: 6px !important;
		border-radius: 50% !important;
		display: inline-block !important;
		margin-right: 4px !important;
		vertical-align: middle !important;
	}

	:global(.tt-time-dot.green) {
		background-color: #16a34a !important;
	}

	:global(.tt-time-dot.red) {
		background-color: #dc2626 !important;
	}

	:global(.tt-td-dur) {
		font-size: 10.5px !important;
		font-weight: 700 !important;
		color: #0369a1 !important;
		text-align: right !important;
	}

	:global(.tt-in-transit) {
		background: #fef3c7 !important;
		color: #92400e !important;
		font-weight: 700 !important;
		font-size: 9px !important;
		padding: 1px 5px !important;
		border-radius: 3px !important;
		border: 1px solid #fde68a !important;
	}

	:global(.tt-docket-tag) {
		background: #e0e7ff !important;
		color: #3730a3 !important;
		font-size: 9px !important;
		font-weight: 700 !important;
		padding: 1px 4px !important;
		border-radius: 3px !important;
		margin-left: 4px !important;
	}

	:global(.tt-truck-popup-footer) {
		display: flex !important;
		align-items: center !important;
		justify-content: space-between !important;
		padding: 6px 14px !important;
		background: #f8fafc !important;
		border-top: 1px solid #e2e8f0 !important;
		font-size: 9.5px !important;
		color: #64748b !important;
		font-weight: 600 !important;
	}
</style>
